// Working hours, time off and holidays in Settings (BL-014 phase 2, on flowology-build's phase 1 server).
// Settings › People › a person's "Hours and time off": the hours they work by weekday from a date on
// (or the company's), what they worked before, and their time off on a leave code, whole days or part
// of one, cancelled with a reason. Settings › Time off and holidays: who is off soon, the company's
// holidays by year, and the non-job codes (an activity the technician starts, or leave the office
// books). Changing hours, time off and holidays is the schedulers' (staff.hours.manage); the codes are
// settings.edit. The dispatch board shows all of it (rs-office1's).

import { useCallback, useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { Sheet } from '../shared/EditSheet.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { useDesign } from '../blueprint/design.ts';
import { Moved } from '../blueprint/Moved.tsx';
import {
  codeOf,
  codeTerms,
  DAYS,
  hoursText,
  timeOffInput,
  weekForm,
  weekText,
  weekWindows,
  type Holiday,
  type NonJobCode,
  type StaffHours,
  type TimeOff,
  type TimeOffForm,
  type WeekForm,
} from './hours-model.ts';
import './setup.css';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');
type Rights = { manage: boolean; codes: boolean };

const todayIn = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );
const plusDays = (date: string, n: number) => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const dayText = (date: string) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${date}T00:00:00Z`));

/** The leave codes time off is booked on. */
function useLeaveCodes() {
  const [codes, setCodes] = useState<NonJobCode[] | null>(null);
  useEffect(() => {
    void client
      .call<{ codes: NonJobCode[] }>('nonJobCodes.list', {})
      .then((r) => setCodes(r.codes.filter((c) => c.category === 'leave')))
      .catch(() => setCodes([]));
  }, []);
  return codes;
}

// ───────── a person's hours and time off ─────────

export function StaffHoursSheet({
  person,
  rights,
  timeZone,
  onClose,
}: {
  person: { staffId: string; displayName: string };
  rights: Rights;
  timeZone: string;
  onClose: () => void;
}) {
  const [said, setSaid] = useState<string | null>(null);
  // time off is ServiceTitan's while the company mirrors it (read here, changed there); working hours
  // stay the company's own (st-mirror §2)
  const mirroring = useMirror();
  // Blueprint: time off is Schedule › Team › Time off's alone (the setup audit); the sheet says where
  const bp = useDesign() === 'blueprint';
  return (
    <Sheet
      title={`${person.displayName}: ${bp ? 'working hours' : 'hours and time off'}`}
      wide
      onClose={onClose}
    >
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
      <WorkingHours person={person} canEdit={rights.manage} timeZone={timeZone} onSaid={setSaid} />
      {bp ? (
        <Moved what="Time off" to="Schedule › Team › Time off" href="#/timeoff" />
      ) : (
        <PersonTimeOff
          person={person}
          canEdit={rights.manage && !mirroring}
          mirrorLine={mirroring && rights.manage}
          timeZone={timeZone}
          onSaid={setSaid}
        />
      )}
      <div className="row-actions">
        <button type="button" className="btn soft" onClick={onClose}>
          Done
        </button>
      </div>
    </Sheet>
  );
}

function WorkingHours({
  person,
  canEdit,
  timeZone,
  onSaid,
}: {
  person: { staffId: string; displayName: string };
  canEdit: boolean;
  timeZone: string;
  onSaid: (t: string) => void;
}) {
  const [h, setH] = useState<StaffHours | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<WeekForm | null>(null);
  const [from, setFrom] = useState(() => todayIn(timeZone));
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setH(await client.call<StaffHours>('staff.hours', { staffId: person.staffId }));
      setError(null);
    } catch (e) {
      setError(say(e));
    }
  }, [person.staffId]);
  useEffect(() => {
    void load();
  }, [load]);
  const save = async (windows: unknown) => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<StaffHours>('staff.setHours', { staffId: person.staffId, windows, from });
      setH(r);
      setForm(null);
      onSaid(
        windows === null
          ? `${person.displayName} works the company’s hours from ${dayText(from)}.`
          : `${person.displayName}’s hours from ${dayText(from)}: ${hoursText(r)}.`,
      );
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (day: number, i: number, k: 'start' | 'end', v: string) =>
    setForm((f) =>
      f!.map((d) =>
        d.day === day ? { ...d, windows: d.windows.map((w, j) => (j === i ? { ...w, [k]: v } : w)) } : d,
      ),
    );
  const built = form ? weekWindows(form) : null;
  return (
    <section className="staff-hours" aria-label={`${person.displayName}’s working hours`}>
      <div className="row between">
        <h3 className="h4">Working hours</h3>
        {canEdit && h && !form && (
          <button type="button" className="btn soft sm" onClick={() => setForm(weekForm(h))}>
            Change hours
          </button>
        )}
      </div>
      {!h ? (
        <p className={error ? 'error small' : 'muted small'}>{error ?? 'Loading their hours…'}</p>
      ) : !form ? (
        <>
          <p>
            {hoursText(h)}
            {h.from && <span className="muted small"> · since {dayText(h.from)}</span>}
          </p>
          {h.history.length > 1 && (
            <details className="hours-history">
              <summary className="small">Earlier hours ({h.history.length - 1})</summary>
              <ul className="licence-list">
                {h.history.slice(1).map((s) => (
                  <li key={s.from} className="small">
                    <span>From {dayText(s.from)}</span>
                    <span className="muted">{s.windows ? weekText(s.windows) : 'The company’s hours'}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : (
        <form
          className="hours-form"
          aria-label={`${person.displayName}’s week`}
          onSubmit={(e) => e.preventDefault()}
        >
          <ul className="hours-days">
            {form.map((d) => (
              <li key={d.day}>
                <b>{DAYS[d.day - 1]}</b>
                {d.windows.length === 0 && <span className="muted small">Off</span>}
                {d.windows.map((w, i) => (
                  <span key={i} className="row">
                    <input
                      className="field time"
                      type="time"
                      aria-label={`${DAYS[d.day - 1]} from`}
                      value={w.start}
                      onChange={(e) => set(d.day, i, 'start', e.target.value)}
                    />
                    <span>to</span>
                    <input
                      className="field time"
                      type="time"
                      aria-label={`${DAYS[d.day - 1]} to`}
                      value={w.end}
                      onChange={(e) => set(d.day, i, 'end', e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn quiet sm"
                      aria-label={`Take off ${DAYS[d.day - 1]}’s ${i === 0 ? 'hours' : 'second window'}`}
                      onClick={() =>
                        setForm((f) =>
                          f!.map((x) =>
                            x.day === d.day ? { ...x, windows: x.windows.filter((_, j) => j !== i) } : x,
                          ),
                        )
                      }
                    >
                      Remove
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className="btn quiet sm"
                  aria-label={`Add hours on ${DAYS[d.day - 1]}`}
                  onClick={() =>
                    setForm((f) =>
                      f!.map((x) =>
                        x.day === d.day
                          ? {
                              ...x,
                              windows: [
                                ...x.windows,
                                x.windows.length
                                  ? { start: x.windows[x.windows.length - 1]!.end, end: '17:00' }
                                  : { start: '08:00', end: '16:30' },
                              ],
                            }
                          : x,
                      ),
                    )
                  }
                >
                  {d.windows.length ? 'Add a window' : 'Works this day'}
                </button>
              </li>
            ))}
          </ul>
          <label className="f">
            From
            <input className="field" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span className="muted small">Days before keep the hours they had.</span>
          </label>
          {built && 'problem' in built && (
            <p className="error small" role="alert">
              {built.problem}
            </p>
          )}
          <div className="row-actions">
            <SaveButton
              busy={busy}
              busyLabel="Saving…"
              error={error}
              disabled={!built || 'problem' in built}
              onClick={() => built && 'windows' in built && void save(built.windows)}
            >
              Save these hours
            </SaveButton>
            {h?.windows && (
              <button type="button" className="btn soft" disabled={busy} onClick={() => void save(null)}>
                Use the company’s hours
              </button>
            )}
            <button type="button" className="btn quiet" onClick={() => setForm(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

const blankTimeOff = (today: string): TimeOffForm => ({
  code: '',
  partDay: false,
  from: today,
  to: today,
  start: '13:00',
  end: '17:00',
  note: '',
});

function PersonTimeOff({
  person,
  canEdit,
  mirrorLine,
  timeZone,
  onSaid,
}: {
  person: { staffId: string; displayName: string };
  canEdit: boolean;
  /** Say why there's no booking: the company mirrors ServiceTitan, which owns time off. */
  mirrorLine: boolean;
  timeZone: string;
  onSaid: (t: string) => void;
}) {
  const today = todayIn(timeZone);
  const [rows, setRows] = useState<TimeOff[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<TimeOffForm | null>(null);
  const [cancelling, setCancelling] = useState<TimeOff | null>(null);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const codes = useLeaveCodes();
  const load = useCallback(async () => {
    try {
      const r = await client.call<{ timeOff: TimeOff[] }>('timeOff.list', {
        staffId: person.staffId,
        from: plusDays(today, -60),
        to: plusDays(today, 365),
        // a cancelled booking stays on their list, with why
        includeCancelled: true,
      });
      setRows(r.timeOff);
      setError(null);
    } catch (e) {
      setError(say(e));
    }
  }, [person.staffId, today]);
  useEffect(() => {
    void load();
  }, [load]);
  const built = form ? timeOffInput(person.staffId, form) : null;
  const add = async () => {
    if (!built || 'problem' in built) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<TimeOff>('timeOff.add', built.input);
      setForm(null);
      onSaid(`Booked for ${person.displayName}: ${r.text}.`);
      await load();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (o: TimeOff) => {
    setBusy(true);
    setError(null);
    try {
      await client.call('timeOff.cancel', { id: o.id, reason: why.trim() });
      setCancelling(null);
      setWhy('');
      onSaid(`Cancelled: ${o.text}.`);
      await load();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const ahead = (rows ?? []).filter((o) => Date.parse(o.end) > Date.now());
  const past = (rows ?? []).filter((o) => Date.parse(o.end) <= Date.now());
  return (
    <section className="staff-hours" aria-label={`${person.displayName}’s time off`}>
      <div className="row between">
        <h3 className="h4">Time off</h3>
        {canEdit && !form && (
          <button type="button" className="btn soft sm" onClick={() => setForm(blankTimeOff(today))}>
            Book time off
          </button>
        )}
      </div>
      {mirrorLine && <p className="muted small">{MIRROR_LINE}</p>}
      {form && (
        <form className="fgrid timeoff-form" aria-label="Book time off" onSubmit={(e) => e.preventDefault()}>
          <label className="f">
            What
            <select
              className="field"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            >
              <option value="">Pick one…</option>
              {(codes ?? []).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                  {c.paid ? '' : ' (unpaid)'}
                </option>
              ))}
            </select>
          </label>
          <label className="checkrow full">
            <input
              type="checkbox"
              checked={form.partDay}
              onChange={(e) => setForm({ ...form, partDay: e.target.checked })}
            />{' '}
            Part of one day
          </label>
          <label className="f">
            {form.partDay ? 'On' : 'From'}
            <input
              className="field"
              type="date"
              value={form.from}
              onChange={(e) =>
                setForm({
                  ...form,
                  from: e.target.value,
                  to: e.target.value > form.to ? e.target.value : form.to,
                })
              }
            />
          </label>
          {form.partDay ? (
            <>
              <label className="f">
                From
                <input
                  className="field time"
                  type="time"
                  value={form.start}
                  onChange={(e) => setForm({ ...form, start: e.target.value })}
                />
              </label>
              <label className="f">
                To
                <input
                  className="field time"
                  type="time"
                  value={form.end}
                  onChange={(e) => setForm({ ...form, end: e.target.value })}
                />
              </label>
            </>
          ) : (
            <label className="f">
              To (the last day off)
              <input
                className="field"
                type="date"
                value={form.to}
                onChange={(e) => setForm({ ...form, to: e.target.value })}
              />
            </label>
          )}
          <label className="f full">
            Note (optional)
            <input
              className="field"
              maxLength={500}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </label>
          {form.code && built && 'problem' in built && (
            <p className="error small full" role="alert">
              {built.problem}
            </p>
          )}
          <div className="row-actions full">
            <SaveButton
              busy={busy}
              busyLabel="Booking…"
              error={error}
              disabled={!built || 'problem' in built}
              onClick={() => void add()}
            >
              Book it
            </SaveButton>
            <button type="button" className="btn quiet" onClick={() => setForm(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {!rows ? (
        <p className={error ? 'error small' : 'muted small'}>{error ?? 'Loading their time off…'}</p>
      ) : !rows.length ? (
        <p className="muted small">None booked.</p>
      ) : (
        <>
          <ul className="licence-list" aria-label="Coming up">
            {ahead.map((o) => (
              <li key={o.id}>
                <span>
                  <b>{o.text}</b>
                  {!o.paid && <span className="muted small"> · unpaid</span>}
                  {o.cancelledAt && <span className="chip say"> Cancelled: {o.cancelReason}</span>}
                  {o.note && <span className="muted small block">{o.note}</span>}
                </span>
                {canEdit && !o.cancelledAt && cancelling?.id !== o.id && (
                  <button
                    type="button"
                    className="btn quiet sm"
                    aria-label={`Cancel ${o.text}`}
                    onClick={() => {
                      setCancelling(o);
                      setWhy('');
                    }}
                  >
                    Cancel…
                  </button>
                )}
                {cancelling?.id === o.id && (
                  <span className="row">
                    <input
                      className="field"
                      aria-label="Why it’s cancelled"
                      placeholder="Why it’s cancelled"
                      maxLength={300}
                      value={why}
                      onChange={(e) => setWhy(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn sm"
                      disabled={!why.trim() || busy}
                      onClick={() => void cancel(o)}
                    >
                      Cancel it
                    </button>
                    <button type="button" className="btn quiet sm" onClick={() => setCancelling(null)}>
                      Keep it
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
          {past.length > 0 && (
            <details>
              <summary className="small">Taken in the last 60 days ({past.length})</summary>
              <ul className="licence-list">
                {past.map((o) => (
                  <li key={o.id} className="small">
                    {o.text}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}

// ───────── Settings › Time off and holidays ─────────

export function TimeTab({ rights, timeZone }: { rights: Rights; timeZone: string }) {
  // holidays and the time codes are ServiceTitan's while the company mirrors it (BL-014)
  const mirroring = useMirror();
  // Blueprint: who's off is Schedule › Time off's, where it's booked (setup audit S025, D3)
  const bp = useDesign() === 'blueprint';
  return (
    <>
      {mirroring && (rights.manage || rights.codes) && <p className="muted small">{MIRROR_LINE}</p>}
      {!bp && <WhoIsOff timeZone={timeZone} />}
      <Holidays canEdit={rights.manage && !mirroring} timeZone={timeZone} />
      <NonJobCodes canEdit={rights.codes && !mirroring} />
    </>
  );
}

function WhoIsOff({ timeZone }: { timeZone: string }) {
  const today = todayIn(timeZone);
  const [rows, setRows] = useState<TimeOff[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void client
      .call<{ timeOff: TimeOff[] }>('timeOff.list', { from: today, to: plusDays(today, 30) })
      .then((r) => setRows(r.timeOff))
      .catch((e: unknown) => setError(say(e)));
  }, [today]);
  return (
    <section className="stone card" aria-label="Who’s off">
      <h2 className="h3">Who’s off in the next 30 days</h2>
      {!rows ? (
        <p className={error ? 'error small' : 'muted small'}>{error ?? 'Loading…'}</p>
      ) : !rows.length ? (
        <p className="muted small">Nobody. Book time off from a person’s hours under People.</p>
      ) : (
        <ul className="licence-list">
          {rows.map((o) => (
            <li key={o.id}>
              <b>{o.staffName}</b>
              <span>
                {o.text}
                {!o.paid && <span className="muted small"> · unpaid</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Holidays({ canEdit, timeZone }: { canEdit: boolean; timeZone: string }) {
  const thisYear = Number(todayIn(timeZone).slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [rows, setRows] = useState<Holiday[] | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<(Holiday & { renaming?: boolean }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // Blueprint: the year's arrows are one control, and a holiday's row has one action (its name renames it)
  const bp = useDesign() === 'blueprint';
  useEffect(() => {
    void client
      .call<{ holidays: Holiday[] }>('holidays.list', { year })
      .then((r) => setRows(r.holidays))
      .catch((e: unknown) => setError(say(e)));
  }, [year, again]);
  const run = async (action: string, input: object, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await client.call(action, input);
      setDraft(null);
      setSaid(done);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="stone card" aria-label="Holidays">
      <div className="row between wrap">
        <h2 className="h3">Holidays</h2>
        <div className="row" {...(bp ? { role: 'group', 'aria-label': 'Year' } : {})}>
          <button
            type="button"
            className="btn quiet sm"
            aria-label="The year before"
            onClick={() => setYear(year - 1)}
          >
            ← {year - 1}
          </button>
          <b>{year}</b>
          <button
            type="button"
            className="btn quiet sm"
            aria-label="The year after"
            onClick={() => setYear(year + 1)}
          >
            {year + 1} →
          </button>
        </div>
      </div>
      <p className="muted small">
        Nobody is booked on a holiday: the board shows it, and booking anyone that day warns.
      </p>
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
      {!rows ? (
        <p className={error ? 'error small' : 'muted small'}>{error ?? 'Loading the holidays…'}</p>
      ) : (
        <ul className="licence-list">
          {!rows.length && <li className="muted small">None set for {year}.</li>}
          {rows.map((h) => (
            <li key={h.date}>
              {bp && canEdit ? (
                <button
                  type="button"
                  className="bp-name"
                  aria-label={`Rename ${h.name}`}
                  onClick={() => setDraft({ ...h, renaming: true })}
                >
                  <b>{dayText(h.date)}</b> {h.name}
                  {!h.paid && <span className="muted small"> · unpaid</span>}
                </button>
              ) : (
                <span>
                  <b>{dayText(h.date)}</b> {h.name}
                  {!h.paid && <span className="muted small"> · unpaid</span>}
                </span>
              )}
              {canEdit && !bp && (
                <span className="row">
                  <button
                    type="button"
                    className="btn quiet sm"
                    aria-label={`Rename ${h.name}`}
                    onClick={() => setDraft({ ...h, renaming: true })}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn quiet sm"
                    aria-label={`Remove ${h.name}`}
                    disabled={busy}
                    onClick={() =>
                      void run('holidays.remove', { date: h.date }, `${h.name} is no longer a holiday.`)
                    }
                  >
                    Remove
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && !draft && (
        <button
          type="button"
          className={bp ? 'btn primary' : 'btn'}
          onClick={() => setDraft({ date: `${year}-01-01`, name: '', paid: true })}
        >
          Add a holiday
        </button>
      )}
      {draft && (
        <form
          className="fgrid"
          aria-label={draft.renaming ? `Edit ${draft.name}` : 'Add a holiday'}
          onSubmit={(e) => e.preventDefault()}
        >
          <label className="f">
            Day
            <input
              className="field"
              type="date"
              disabled={draft.renaming}
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
          </label>
          <label className="f">
            Name
            <input
              className="field"
              maxLength={80}
              placeholder="Thanksgiving"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label className="checkrow full">
            <input
              type="checkbox"
              checked={draft.paid}
              onChange={(e) => setDraft({ ...draft, paid: e.target.checked })}
            />{' '}
            Paid
          </label>
          <div className="row-actions full">
            <SaveButton
              busy={busy}
              busyLabel="Saving…"
              error={error}
              disabled={!draft.name.trim() || !draft.date}
              onClick={() =>
                void run(
                  'holidays.set',
                  { date: draft.date, name: draft.name.trim(), paid: draft.paid },
                  `${draft.name.trim()} on ${dayText(draft.date)} saved.`,
                )
              }
            >
              Save the holiday
            </SaveButton>
            <button type="button" className="btn quiet" onClick={() => setDraft(null)}>
              Cancel
            </button>
            {/* Blueprint: a holiday's row opens it, and it's removed from here */}
            {bp && draft.renaming && (
              <button
                type="button"
                className="btn quiet"
                disabled={busy}
                onClick={() =>
                  void run('holidays.remove', { date: draft.date }, `${draft.name} is no longer a holiday.`)
                }
              >
                It’s not a holiday
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

type CodeDraft = NonJobCode & { isNew: boolean };

function NonJobCodes({ canEdit }: { canEdit: boolean }) {
  const [rows, setRows] = useState<NonJobCode[] | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<CodeDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // Blueprint: Add a code is the section's one primary
  const bp = useDesign() === 'blueprint';
  useEffect(() => {
    void client
      .call<{ codes: NonJobCode[] }>('nonJobCodes.list', { includeInactive: true })
      .then((r) => setRows(r.codes))
      .catch((e: unknown) => setError(say(e)));
  }, [again]);
  const save = async (d: CodeDraft) => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<NonJobCode>('nonJobCodes.save', {
        code: d.isNew ? codeOf(d.name) : d.code,
        name: d.name.trim(),
        category: d.category,
        paid: d.paid,
        countsTowardOvertime: d.countsTowardOvertime,
        active: d.active,
      });
      setDraft(null);
      setSaid(`${r.name} saved.`);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  const group = (category: NonJobCode['category']) => (rows ?? []).filter((c) => c.category === category);
  return (
    <section className="stone card" aria-label="Time codes">
      <div className="row between wrap">
        <h2 className="h3">Time that isn’t on a job</h2>
        {canEdit && !draft && (
          <button
            type="button"
            className={bp ? 'btn primary' : 'btn'}
            onClick={() =>
              setDraft({
                code: '',
                name: '',
                category: 'leave',
                paid: true,
                countsTowardOvertime: false,
                active: true,
                isNew: true,
              })
            }
          >
            Add a code
          </button>
        )}
      </div>
      <p className="muted small">
        Activities a technician starts on the iPad (lunch, the shop), and leave the office books for them
        (vacation, sick). Leave never reaches the iPad’s list.
      </p>
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
      {!rows ? (
        <p className={error ? 'error small' : 'muted small'}>{error ?? 'Loading the codes…'}</p>
      ) : (
        (['activity', 'leave'] as const).map((cat) => (
          <div key={cat}>
            <h3 className="h4">{cat === 'activity' ? 'Activities' : 'Leave'}</h3>
            <ul className="licence-list">
              {group(cat).map((c) => (
                <li key={c.code}>
                  <span>
                    <b>{c.name}</b> <span className="muted small">{codeTerms(c)}</span>
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn quiet sm"
                      aria-label={`Edit ${c.name}`}
                      onClick={() => setDraft({ ...c, isNew: false })}
                    >
                      Edit
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
      {draft && (
        <form
          className="fgrid"
          aria-label={draft.isNew ? 'Add a code' : `Edit ${draft.name}`}
          onSubmit={(e) => e.preventDefault()}
        >
          <label className="f">
            Name
            <input
              className="field"
              maxLength={60}
              placeholder="Bereavement"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label className="f">
            Kind
            <select
              className="field"
              value={draft.category}
              onChange={(e) => {
                const category = e.target.value as NonJobCode['category'];
                setDraft({ ...draft, category, countsTowardOvertime: category === 'activity' });
              }}
            >
              <option value="leave">Leave the office books</option>
              <option value="activity">An activity the technician starts</option>
            </select>
          </label>
          <label className="checkrow full">
            <input
              type="checkbox"
              checked={draft.paid}
              onChange={(e) => setDraft({ ...draft, paid: e.target.checked })}
            />{' '}
            Paid
          </label>
          <label className="checkrow full">
            <input
              type="checkbox"
              checked={draft.countsTowardOvertime}
              onChange={(e) => setDraft({ ...draft, countsTowardOvertime: e.target.checked })}
            />{' '}
            Counts toward overtime
          </label>
          {!draft.isNew && (
            <label className="checkrow full">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
              />{' '}
              In use (off: retired, kept on past time)
            </label>
          )}
          <div className="row-actions full">
            <SaveButton
              busy={busy}
              busyLabel="Saving…"
              error={error}
              disabled={!draft.name.trim()}
              onClick={() => void save(draft)}
            >
              Save the code
            </SaveButton>
            <button type="button" className="btn quiet" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
